import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Path, PlayCircle, Question, Target, type Icon } from '@phosphor-icons/react';
import type { InterviewQuestion, ModuleProgressState, ModuleView } from '@cvs/shared';
import { api } from '../lib/api';
import { ALL_COURSES, useCourse } from '../lib/course';
import { useAsync } from '../lib/useAsync';
import { formatDate, pct } from '../lib/format';
import { Card, ErrorState, LoadingState, Pill, ProgressBar, SectionHeading } from '../components/ui';

const CTA_LABEL: Record<Exclude<ModuleProgressState, 'locked'>, string> = {
  available: 'Start',
  'in-progress': 'Continue',
  completed: 'Review',
};

const CTA_ICON: Record<Exclude<ModuleProgressState, 'locked'>, Icon> = {
  available: PlayCircle,
  'in-progress': PlayCircle,
  completed: CheckCircle,
};

export function MyProgram() {
  const { currentCourse, currentSlug, coursePath } = useCourse();
  const s = useAsync(() => api.program(), []);

  if (s.loading) return <LoadingState />;
  if (s.error || !s.data) return <ErrorState message={s.error ?? 'Failed to load'} onRetry={s.reload} />;

  const { batch, sections, modules, projects } = s.data;
  // Heading reflects the selected course (not a hardcoded path); the cohort/start
  // date, when known, is a supporting detail.
  const courseTitle =
    currentSlug === ALL_COURSES ? 'All courses' : currentCourse?.title ?? batch?.name ?? 'Your program';
  const headingBits = [
    currentCourse?.weeks ? `${currentCourse.weeks} weeks` : null,
    batch?.name ?? null,
    batch ? `started ${formatDate(batch.startDate)}` : null,
  ].filter(Boolean);

  const totals = modules.reduce(
    (acc, m) => {
      acc.completed += m.completedLessons;
      acc.total += m.totalLessons;
      return acc;
    },
    { completed: 0, total: 0 },
  );
  const overallPct = totals.total === 0 ? 0 : (totals.completed / totals.total) * 100;
  const sectionIds = new Set(sections.map((sec) => sec.id));
  const unfiled = modules.filter((m) => !m.sectionId || !sectionIds.has(m.sectionId));
  // The class to resume: an in-progress one, else the next available — among
  // published classes only (a class being prepared can't be started yet).
  const nextClass =
    modules.find((m) => m.status === 'published' && m.state === 'in-progress') ??
    modules.find((m) => m.status === 'published' && m.state === 'available') ??
    null;

  return (
    <div className="space-y-6">
      <SectionHeading eyebrow="Curriculum" title={courseTitle} description={headingBits.join(' · ')} />

      {/* Resume point — so learners don't scan weeks to find where they were. */}
      {nextClass && (
        <Card className="flex flex-wrap items-center gap-4 p-5">
          <div className="min-w-0 flex-1">
            <div className="label-mono text-brand-teal">
              {nextClass.state === 'in-progress' ? 'Continue where you left off' : 'Start here'}
            </div>
            <div className="mt-1 truncate font-bold text-brand-navy">{nextClass.title}</div>
          </div>
          <Link to={coursePath(`classes/${nextClass.id}`)} className="btn-primary shrink-0">
            <PlayCircle size={18} weight="bold" />
            {nextClass.state === 'in-progress' ? 'Continue' : 'Start'}
          </Link>
        </Card>
      )}

      {/* Overall progress summary */}
      <Card className="p-6">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-50 text-teal-700">
              <Path size={18} weight="bold" />
            </span>
            <span className="label-mono">Overall progress</span>
          </div>
          <div className="text-sm text-slate-500">
            <span className="text-lg font-bold text-brand-navy">{pct(overallPct)}</span> complete ·{' '}
            {totals.completed} / {totals.total} lessons
          </div>
        </div>
        <ProgressBar value={totals.completed} max={totals.total} />
      </Card>

      {/* Roadmap: sections → classes → project. Empty sections are hidden from
          learners, so numbering runs over the visible ones only. */}
      <div className="space-y-8">
        {sections
          .map((sec) => ({
            sec,
            classes: modules.filter((m) => m.sectionId === sec.id),
            project: projects.find((p) => p.sectionId === sec.id) ?? null,
          }))
          .filter(({ classes, project }) => classes.length > 0 || project)
          .map(({ sec, classes, project }, si) => {
          const done = classes.every((c) => c.state === 'completed') && classes.length > 0;
          return (
            <section key={sec.id}>
              <div className="mb-3 flex items-center gap-3">
                <span className="rounded-full border border-brand-teal/40 px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-brand-teal">
                  Section {si + 1}
                </span>
                <h2 className="min-w-0 flex-1 truncate text-lg font-bold text-brand-navy">{sec.title}</h2>
                <span className="text-xs text-slate-400">
                  {classes.filter((c) => c.state === 'completed').length}/{classes.length} classes done
                </span>
              </div>
              <ol className="space-y-4">
                {classes.map((m, ci) => (
                  <li key={m.id}>
                    <ModuleCard view={m} label={`${si + 1}.${ci + 1}`} />
                  </li>
                ))}
              </ol>
              {project && (
                <div className="mt-4 flex flex-wrap items-center gap-4 rounded-2xl border-2 border-dashed border-brand-teal/50 bg-brand-teal/5 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-teal text-white">
                    <Target size={22} weight="bold" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-bold text-brand-navy">{project.title}</h3>
                      <Pill tone="teal">{project.points} pts</Pill>
                    </div>
                    <p className="mt-1 text-sm text-slate-500">
                      {done
                        ? 'You finished this section — time for the project! Submit it from Labs & Projects.'
                        : 'Section project — do this after finishing the classes above.'}
                    </p>
                  </div>
                  <Link to={coursePath('labs')} className={done ? 'btn-primary' : 'btn-secondary'}>
                    {done ? 'Start the project' : 'View project'}
                  </Link>
                </div>
              )}
              <SectionInterview questions={sec.interviewQuestions ?? []} />
            </section>
          );
        })}

        {unfiled.length > 0 && (
          <section>
            <ol className="space-y-4">
              {unfiled.map((m, i) => (
                <li key={m.id}>
                  <ModuleCard view={m} label={String(i + 1)} />
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}

/** Compact pointer to the section's interview Q&A — practised in Interview Prep,
 *  so the questions live in one place (the interactive "Start Q&A" flow) instead
 *  of being duplicated inline here. */
function SectionInterview({ questions }: { questions: InterviewQuestion[] }) {
  if (questions.length === 0) return null;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-700">
        <Question size={22} weight="bold" />
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-base font-bold text-brand-navy">Interview questions</h3>
        <p className="text-sm text-slate-500">
          {questions.length} question{questions.length > 1 ? 's' : ''} on this topic — practise them with a
          suggested-answer reveal in Interview Prep.
        </p>
      </div>
      <Link to="/interview" className="btn-secondary shrink-0">
        Practise <ArrowRight size={18} weight="bold" />
      </Link>
    </div>
  );
}

function ModuleCard({ view, label }: { view: ModuleView; label: string }) {
  const [showAllTopics, setShowAllTopics] = useState(false);
  const visibleTopics = showAllTopics ? view.topics : view.topics.slice(0, 2);
  const remainingTopics = view.topics.length - visibleTopics.length;
  // Students see the learner state through the Start/Continue/Review button, not
  // instructor publishing pills. A class that isn't published yet is "being prepared".
  const isReady = view.status === 'published';

  return (
    <Card className="p-5 transition">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        {/* Position badge (section.class) */}
        <span
          className="flex h-11 min-w-11 shrink-0 items-center justify-center rounded-xl bg-brand-teal/15 px-2 text-lg font-extrabold text-brand-teal"
          aria-hidden="true"
        >
          {label}
        </span>

        <div className="min-w-0 flex-1">
          <h3 className="text-base font-bold text-brand-navy">{view.title}</h3>

          {view.topics.length > 0 && (
            <div className="mt-3">
              <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">You'll cover</p>
              <ul className="space-y-1 text-sm leading-snug text-slate-600">
                {visibleTopics.map((t) => (
                  <li key={t} className="flex gap-2">
                    <span className="mt-[0.45rem] h-1 w-1 shrink-0 rounded-full bg-brand-teal/70" aria-hidden="true" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              {remainingTopics > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAllTopics(true)}
                  className="mt-2 text-xs font-semibold text-brand-teal transition hover:text-brand-navy hover:underline"
                >
                  Show {remainingTopics} more topic{remainingTopics === 1 ? '' : 's'}
                </button>
              )}
              {showAllTopics && view.topics.length > 2 && (
                <button
                  type="button"
                  onClick={() => setShowAllTopics(false)}
                  className="mt-2 text-xs font-semibold text-slate-400 transition hover:text-brand-navy hover:underline"
                >
                  Show less
                </button>
              )}
            </div>
          )}

          {isReady ? (
            <div className="mt-4">
              <div className="mb-1 flex justify-between text-xs font-semibold text-slate-500">
                <span>
                  {view.completedLessons} of {view.totalLessons} class resource{view.totalLessons === 1 ? '' : 's'}{' '}
                  complete
                </span>
              </div>
              <ProgressBar value={view.completedLessons} max={view.totalLessons} />
            </div>
          ) : (
            <p className="mt-4 text-sm font-medium text-slate-500">This class is being prepared.</p>
          )}
        </div>

        {/* CTA — only for ready (published) classes; a class being prepared has nothing to start yet. */}
        <div className="shrink-0 sm:self-center">{isReady && <ModuleCta view={view} />}</div>
      </div>
    </Card>
  );
}

function ModuleCta({ view }: { view: ModuleView }) {
  const { coursePath } = useCourse();
  const state = view.state as Exclude<ModuleProgressState, 'locked'>;
  const IconCmp = CTA_ICON[state];
  const className = state === 'available' ? 'btn-primary' : 'btn-secondary';
  return (
    <Link to={coursePath(`classes/${view.id}`)} className={className}>
      <IconCmp size={18} weight="bold" />
      {CTA_LABEL[state]}
    </Link>
  );
}
